export type LiveCallMode = 'video';

export interface LiveClaimRequest {
  call_mode: LiveCallMode;
  avatar_id: string;
}

export interface LiveClaimInfo {
  id: string;
  stream_url: string;
  live_duration: number;
  status: string;
}

export interface LiveClaimRtcInfo {
  app_id: string;
  channel_id: string;
  nonce?: string;
  role?: number | string;
  session_id?: string;
  timestamp?: number | string;
  user_id: string;
  token: string;
  token_expire_at: string;
}

export interface LiveClaimResponse {
  live: LiveClaimInfo;
  is_success: boolean;
  rtc: LiveClaimRtcInfo;
}

export interface LiveClaimErrorPayload {
  code?: number;
  message?: string;
  reason?: string;
}

export type LiveClaimErrorCode =
  | 'claim_not_enough_quota'
  | 'invalid_claim_response'
  | 'session_store_failed'
  | 'unauthorized'
  | 'request_failed';

export class LiveClaimError extends Error {
  code: LiveClaimErrorCode;
  reason?: string;
  status?: number;

  constructor({
    code,
    message,
    reason,
    status,
  }: {
    code: LiveClaimErrorCode;
    message: string;
    reason?: string;
    status?: number;
  }) {
    super(message);
    this.name = 'LiveClaimError';
    this.code = code;
    this.reason = reason;
    this.status = status;
  }
}
/**
 * 直播调试链路跨路由传递的最小元数据。
 * - session_correlation_id：一次通话的前端追踪 id，claim 侧生成，直播页恢复复用
 * - avatar_type：直播页恢复 logger context 时需要，按 UGC/PGC 聚合事件
 * - claim_request_started_at_perf_ms / claim_success_at_perf_ms：单调时钟里程碑，仅同 document 有效；
 * - claim_request_started_at_perf_ms：麦克风授权完成后、claimLive 请求发起时的单调时钟里程碑；
 *   performance 不可用或跨 document 刷新时直接省略，不写占位值；
 *   直播页恢复前需 `Number.isFinite` 校验，缺失时直接省略对应耗时字段。
 * 只允许可序列化原始字段；logger 实例不在此保存。
 */
export interface LiveClaimDebugMetadata {
  session_correlation_id: string;
  avatar_type: 'ugc' | 'pgc';
  claim_request_started_at_perf_ms?: number;
  claim_success_at_perf_ms?: number;
}

export interface StoredLiveClaimSession {
  avatarId: string;
  /** 预留头像图 URL；接口补充该字段后即可在 connecting 态直接展示。 */
  avatarUrl?: string;
  claimedAt: string;
  claim: LiveClaimResponse;
  /** 调试链路元数据；老流程/刷新场景可为空，直播页需要 fallback。 */
  debugMetadata?: LiveClaimDebugMetadata;
}

/**
 * 通话模块使用的头像实体，对齐 Android AvatarDetailBean 的展示相关字段。
 * 仅保留 UI 展示需要的字段，后端补充头像接口后可直接映射。
 */
export type {
  LiveV1Avatar,
  LiveV1AvatarRole as LiveAvatar,
  LiveV1AvatarVoiceLibrary,
  LiveV1AvatarWithVoice,
  LiveV1CreatePersonalVoiceReply,
  LiveV1CreateVoiceCloneReply,
  LiveV1GetLivePlaybackShareDetailReply,
  LiveV1LivePlayback,
  LiveV1VoiceLibrary,
} from '@/app/services/live';

/** 通话页非主动退出的原因；返回 /live 后据此给特殊提示（对齐 iOS / Android 的挂断提示机制）。 */
export type LiveExitReason =
  | 'duration_limit'
  | 'connect_timeout'
  | 'connection_lost'
  | 'server_force_hangup'
  | 'timeout_hangup'
  | 'audit_rejected'
  | 'internal_fail'
  | 'ended'
  | 'realtime_model_fail'
  | 'prepared_timeout'
  | 'user_hangup'
  | 'app_close_conn'
  | 'prepared_sip_disconnected'
  | 'sip_reconnect_timeout';

/**
 * 一次退出提示。reason 决定兜底文案（live.exitNotice.{reason}）；
 * message 为服务端 force_hangup 等下发的自定义文案，非空时优先显示。
 */
export interface LiveExitNotice {
  reason: LiveExitReason;
  message?: string;
}
